import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, type CollectionResponse, type ResourceResponse } from '@shared/api/client';
import type { Issuer } from '../types/issuer';
import type { IssuerFormData } from '../schemas/issuer.schema';

const BASE = '/billing/issuers';

export const issuerKeys = {
  all: ['issuers'] as const,
  list: () => [...issuerKeys.all, 'list'] as const,
  detail: (id: number) => [...issuerKeys.all, 'detail', id] as const,
};

export function useIssuers() {
  return useQuery({
    queryKey: issuerKeys.list(),
    queryFn: async () => {
      const { data } = await api.get<CollectionResponse<Issuer>>(BASE);
      return data.data;
    },
  });
}

export function useIssuer(id: number) {
  return useQuery({
    queryKey: issuerKeys.detail(id),
    queryFn: async () => {
      const { data } = await api.get<ResourceResponse<Issuer>>(`${BASE}/${id}`);
      return data.data;
    },
    enabled: id > 0,
  });
}

export function useCreateIssuer() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: IssuerFormData) => {
      const { data } = await api.post<ResourceResponse<Issuer>>(BASE, payload);
      return data.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: issuerKeys.list() });
    },
  });
}

export function useUpdateIssuer() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, payload }: { id: number; payload: Partial<IssuerFormData> }) => {
      const { data } = await api.put<ResourceResponse<Issuer>>(`${BASE}/${id}`, payload);
      return data.data;
    },
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: issuerKeys.list() });
      qc.invalidateQueries({ queryKey: issuerKeys.detail(vars.id) });
    },
  });
}

export interface IssuerSmtpPayload {
  mail_transport?: "smtp" | "ses";
  mail_host?: string | null;
  mail_port?: number | null;
  mail_username?: string | null;
  /** Vacío = mantener la contraseña actual. */
  mail_password?: string | null;
  mail_encryption?: "tls" | "ssl" | null;
  mail_from_address?: string | null;
  mail_from_name?: string | null;
}

export function useUpdateIssuerSmtp() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, payload }: { id: number; payload: IssuerSmtpPayload }) => {
      const { data } = await api.patch<ResourceResponse<Issuer>>(
        `${BASE}/${id}/smtp`,
        payload,
      );
      return data.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: issuerKeys.list() });
    },
  });
}

export function useTestIssuerSmtp() {
  return useMutation({
    mutationFn: async ({ id, email }: { id: number; email: string }) => {
      const { data } = await api.post<{ ok: boolean; email?: string; message?: string }>(
        `${BASE}/${id}/smtp/test`,
        { email },
      );
      return data;
    },
  });
}

export function useDeleteIssuer() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number) => {
      await api.delete(`${BASE}/${id}`);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: issuerKeys.list() });
    },
  });
}

/** Sube un logo y devuelve su ruta pública relativa + URL absoluta. */
export async function uploadIssuerLogo(file: File): Promise<{ path: string; url: string }> {
  const fd = new FormData();
  fd.append("logo", file);
  const { data } = await api.post<{ path: string; url: string }>(`${BASE}/logo`, fd, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return data;
}

/**
 * Devuelve el siguiente número de factura que se asignará al emisor.
 * Sirve para previsualizarlo en el formulario antes de guardar.
 */
export function useNextInvoiceNumber(emisorId: number | null | undefined) {
  return useQuery({
    queryKey: [...issuerKeys.all, 'next-number', emisorId ?? 0] as const,
    queryFn: async () => {
      const { data } = await api.get<{ numero: string }>(
        `${BASE}/${emisorId}/next-number`,
      );
      return data.numero;
    },
    enabled: !!emisorId,
    staleTime: 30_000,
  });
}
